import React, { useState, useEffect } from "react";
import { HelpCircle } from "lucide-react";
import { SimpleTooltip } from "./design-system";

// Tooltip definitions
const tooltipDefinitions = {
  PTS: "A player's predicted points per 100 possessions based on predicted shot usage and efficiency.",
  "2PT%":
    "A player's predicted two-point shooting percentage against an average opponent, adjusted for usage.",
  "3PT%":
    "A player's predicted three-point shooting percentage against an average opponent, adjusted for usage.",
  "FT%":
    "A player's predicted free throw shooting percentage against an average opponent.",
  REB: "A player's predicted offensive + defensive rebounding rate. See O-Reb % and D-Reb % for more detail.",
  AST: "A player's predicted assist rate against an average opponent, adjusted for usage. Assist Rate is the percentage of teammate made field goals that the player assisted while on the court.",
  "Steal Rate":
    "A player's predicted steal percentage against an average opponent. Steal percentage is percentage of defensive possessions that end in the player stealing the ball while on the court.",
  BLK: "A player's predicted block percentage against an average opponent, adjusted for usage.. Block percentage is the percentage of opponent two-point field goal attempts that the player blocked while on the court.",
  "Turn Over":
    "A player's predicted turnover percentage against an average opponent, adjusted for usage. Turnover percentage is the percentage of offensive possessions that end in the player turning the ball over while on the court.",
  PF: "A player's predicted personal fouls committed per 100 possessions.",
  Season:
    "The specific basketball season (year) in which the player's predicted stats and values are calculated.",
  Poss: "The number of possessions that a player is predicted to be involved in. A possession refers to a team's opportunity to score before the other team gains control of the ball.",
  "+/-":
    "A player's predicted plus-minus rating, which measures the point differential (points scored by the player's team minus points scored by the opponent) per 100 possessions while the player is on the court, against an average opponent.",
  Team: "The team that the player is predicted to play for in the given season.",
};

const teamStatisticsLegend = [
  { key: "Season", value: "Basketball season year" },
  { key: "Team", value: "Team name or abbreviation" },
  { key: "Poss", value: "Total team possessions count" },
  { key: "+/-", value: "On-court point differential" },
  { key: "PTS", value: "Total points scored" },
  { key: "2PT%", value: "Predicted two-point percentage" },
  { key: "3PT%", value: "Predicted three-point percentage" },
  { key: "FT%", value: "Predicted free throw percentage" },
  { key: "REB", value: "Total rebounds by team" },
  { key: "AST", value: "Total assists by team" },
  { key: "Steal Rate", value: "Opponent possession steal percentage" },
  { key: "BLK", value: "Total blocked shots" },
  { key: "Turn Over", value: "Total turnovers by team" },
  { key: "PF", value: "Total personal fouls committed" },
];

const StatisticsTable = ({ player }) => {
  const TableHeader = ({ children, hasTooltip = false }) => (
    <th
      className="py-3 px-4 text-left font-semibold text-[#999999]"
    >
      <div className="flex items-center gap-2">
        <span>{children}</span>
        {hasTooltip && tooltipDefinitions[children] && (
          <SimpleTooltip title={tooltipDefinitions[children]}>
            <HelpCircle
              size={12}
              style={{
                color: "#9ca3af",
                opacity: 0.7,
                cursor: "help",
              }}
            />
          </SimpleTooltip>
        )}
      </div>
    </th>
  );

  return (
    <>
      {/* Removed custom tooltip */}
      <div
        className="mt-4 bg-[#0A0A0A] rounded-[14px] shadow-sm border border-[#1A1A1A] overflow-hidden"
      >
        <div className="p-6">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead
                className="bg-[#111111] text-xs uppercase tracking-wider"
              >
                <tr>
                  <TableHeader hasTooltip>Season</TableHeader>
                  <TableHeader hasTooltip>Team</TableHeader>
                  <TableHeader hasTooltip>Poss</TableHeader>
                  <TableHeader hasTooltip>+/-</TableHeader>
                  <TableHeader hasTooltip>PTS</TableHeader>
                  <TableHeader hasTooltip>2PT%</TableHeader>
                  <TableHeader hasTooltip>3PT%</TableHeader>
                  <TableHeader hasTooltip>FT%</TableHeader>
                  <TableHeader hasTooltip>REB</TableHeader>
                  <TableHeader hasTooltip>AST</TableHeader>
                  <TableHeader hasTooltip>Steal Rate</TableHeader>
                  <TableHeader hasTooltip>BLK</TableHeader>
                  <TableHeader hasTooltip>Turn Over</TableHeader>
                  <TableHeader hasTooltip>PF</TableHeader>
                </tr>
              </thead>
              <tbody
                className="bg-[#0A0A0A] divide-y divide-[#1A1A1A]"
              >
                <tr
                  className="hover:bg-[rgba(255,255,255,0.04)] transition-colors"
                >
                  <td
                    className="py-3 px-4 text-[#999999] font-medium"
                  >
                    2024-2025
                  </td>
                  <td
                    className="py-3 px-4 text-[#999999]"
                  >
                    {player?.team || "N/A"}
                  </td>
                  <td
                    className="py-3 px-4 text-[#999999]"
                  >
                    {player?.possessions || "N/A"}
                  </td>
                  <td
                    className="py-3 px-4 text-[#999999]"
                  >
                    {player?.plusMinus || "N/A"}
                  </td>
                  <td
                    className="py-3 px-4 text-[#999999] font-medium"
                  >
                    {player?.scoring || "N/A"}
                  </td>
                  <td
                    className="py-3 px-4 text-[#999999]"
                  >
                    {player?.twoPointPct || "N/A"}
                  </td>
                  <td
                    className="py-3 px-4 text-[#999999]"
                  >
                    {player?.threePointPct || "N/A"}
                  </td>
                  <td
                    className="py-3 px-4 text-[#999999]"
                  >
                    {player?.ftPct || "N/A"}
                  </td>
                  <td
                    className="py-3 px-4 text-[#999999]"
                  >
                    {player?.rebPct || "N/A"}
                  </td>
                  <td
                    className="py-3 px-4 text-[#999999]"
                  >
                    {player?.assistRate || "N/A"}
                  </td>
                  <td
                    className="py-3 px-4 text-[#999999]"
                  >
                    {player?.stealRate || "N/A"}
                  </td>
                  <td
                    className="py-3 px-4 text-[#999999]"
                  >
                    {player?.blockPct || "N/A"}
                  </td>
                  <td
                    className="py-3 px-4 text-[#999999]"
                  >
                    {player?.turnoverRate || "N/A"}
                  </td>
                  <td
                    className="py-3 px-4 text-[#999999]"
                  >
                    {player?.personalFouls || "N/A"}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div
        className="mt-4 bg-[#0A0A0A] rounded-[14px] shadow-sm border border-[#1A1A1A] p-4"
      >
        <h3
          className="text-lg font-semibold mb-3 text-[#F5F5F5]"
        >
          Team Statistics Legend
        </h3>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-5">
          {teamStatisticsLegend.map((item, index) => (
            <div
              key={index}
              className="p-2 rounded bg-[#111111]"
            >
              <div
                className="font-medium text-sm text-[#F5F5F5]"
              >
                {item.key}
              </div>
              <div
                className="text-xs mt-1 text-[#555555]"
              >
                {item.value}
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default StatisticsTable;
